import getUrls from "get-urls";

// Helper function to check if a URL is already wrapped in an anchor tag
const isUrlInAnchorTag = (text) => {
  const anchorTagRegex =
    /<a\s+(?:[^>]*?\s+)?href=['"]*([^'">\s]+)['"]*[^>]*>.*?<\/a>/g;
  return anchorTagRegex.test(text);
};

// Helper function to validate if a URL is properly formatted
const isValidUrl = (url) => {
  try {
    const parsedUrl = new URL(url);
    return (
      (parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:") &&
      parsedUrl.hostname.includes(".")
    );
  } catch {
    return false;
  }
};

// Helper function to extract standalone URLs (not in anchor tags)
const extractStandaloneUrls = (text) => {
  // Temporarily replace anchor tags with placeholders
  let processedText = text;
  const anchorTags = [];
  const anchorTagRegex =
    /(<a\s+(?:[^>]*?\s+)?href=['"]*([^'">\s]+)['"]*[^>]*>.*?<\/a>)/g;

  processedText = processedText.replace(anchorTagRegex, (match) => {
    anchorTags.push(match);
    return `##ANCHOR${anchorTags.length - 1}##`;
  });

  // Refined URL regex to ensure URLs start with valid protocols or 'www.'
  const urlRegex =
    /(http(s)?:\/\/|www\.)[a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g;

  const matches = processedText.match(urlRegex) || [];

  // Restore anchor tags
  anchorTags.forEach((tag, index) => {
    processedText = processedText.replace(`##ANCHOR${index}##`, tag);
  });

  // Filter valid URLs
  return matches.filter((url) => isValidUrl(url));
};

// Function to check if a string contains valid URLs
export const isLink = (pastedData) => {
  if (pastedData && typeof pastedData === "string") {
    const standaloneUrls = extractStandaloneUrls(pastedData);
    return standaloneUrls.length > 0;
  }
  return false;
};

// Function to extract unique links from a string
const getLink = (pastedData) => {
  const standaloneUrls = extractStandaloneUrls(pastedData);
  return new Set(standaloneUrls);
};

// Function to generate anchor tags for valid links
const generateLink = (link) => {
  let decode_link = decodeURI(link);
  let titleAttribute = `title=""`;

  if (decode_link.includes("knowledge")) {
    titleAttribute = `title="Before you click on the link, please make sure you have Knowledge open."`;
  }

  if (decode_link.includes("template=")) {
    let title = decode_link.split("template=")[1].replace(/ /g, "_");
    decode_link = decode_link.split("template=")[0];
    decode_link = decode_link.split("&")[0];
    return `<a href="${decode_link}" target="_blank" ${titleAttribute}>${title.replace(
      /_/g,
      " "
    )}</a>`;
  } else if (decode_link.includes("title=")) {
    decode_link = decode_link.replace("#", "/#");
    let title = decode_link.split("title=")[1].replace(/ /g, "_");
    return `<a href="${decode_link}" target="_blank" ${titleAttribute}>${title.replace(
      /_/g,
      " "
    )}</a>`;
  } else if (decode_link.includes("caseName=")) {
    let title = decode_link.split("caseName=")[1].replace(/ /g, "_");
    decode_link = decode_link.split("caseName=")[0];
    decode_link = decode_link.split("&")[0];
    return `<a href="${decode_link}" target="_blank" className="hand-cursor" ${titleAttribute}>${title.replace(
      /_/g,
      " "
    )}</a>`;
  }
  if (decode_link.includes("playbookName=")) {
    let title = decode_link.split("playbookName=")[1].replace(/ /g, "_");
    decode_link = decode_link.split("playbookName=")[0];
    decode_link = decode_link.split("&")[0];
    return `<a href="${decode_link}" target="_blank" className="hand-cursor" ${titleAttribute}>${title.replace(
      /_/g,
      " "
    )}</a>`;
  } else {
    return `<a href="${decode_link}" className="hand-cursor" target="_blank" ${titleAttribute}>Click Here</a>`;
  }
};

// Main function to process links in text and wrap them in anchor tags
export const processLink = (data) => {
  // Early return if the text is entirely within an anchor tag
  if (data.trim().startsWith("<a") && data.trim().endsWith("</a>")) {
    return data;
  }

  let isValidLink = isLink(data);

  if (isValidLink) {
    let links = getLink(data);
    links = Array.from(links);
    links = links.map((d) => d.replace("#/", "/#/"));
    links = links.map((d) => d.replace("//#/", "/#/"));

    links.forEach((link) => {
      let decode_link = decodeURIComponent(link);
      decode_link = decode_link.replace("</p>", "");

      // Create a RegExp that matches the URL only if it's not within an anchor tag
      const safeReplaceRegex = new RegExp(
        `(?<!<a[^>]*?href=['"]?[^'"\\s>]*?)${decode_link.replace(
          /[.*+?^${}()|[\]\\]/g,
          "\\$&"
        )}`,
        "g"
      );

      let url = generateLink(decode_link);
      if (decode_link.includes("title=")) {
        decode_link = decode_link.replace("#", "/#");
      }

      // Only replace URLs that are not within anchor tags
      data = data.replace(safeReplaceRegex, url);
    });
  }

  return data;
};
